import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { coachingAPI } from '../utils/api';
import { ArrowLeft, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CoachingApplicationPage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    goals: '',
    what_brought_you: '',
    availability: '',
    budget_expectations: '',
    additional_info: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.goals || !formData.what_brought_you || !formData.availability || !formData.budget_expectations) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await coachingAPI.apply(formData);
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <nav className="bg-card border-b border-border sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <Link to="/" className="text-xl font-serif text-foreground">
              ClarityPath <span className="text-muted-foreground">—</span> <span className="text-accent">Guided by Mo</span>
            </Link>
          </div>
        </nav>
        <div className="max-w-xl mx-auto px-6 py-24 text-center space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10">
            <CheckCircle2 className="text-accent" size={32} />
          </div>
          <h1 className="text-3xl font-serif" data-testid="application-success-heading">Thank you for applying.</h1>
          <p className="text-muted-foreground leading-relaxed">
            Mo will review your application personally and reach out soon. 
            A confirmation has been sent to your email.
          </p>
          <p className="text-muted-foreground text-sm">
            In the meantime, feel free to explore ClarityPath and start your journey with our free tools.
          </p>
          <Button onClick={() => navigate('/signup')} className="rounded-full mt-4" data-testid="get-started-after-apply">
            Create Your Free Account
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-xl font-serif text-foreground">
              ClarityPath <span className="text-muted-foreground">—</span> <span className="text-accent">Guided by Mo</span>
            </Link>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="rounded-full">
                <ArrowLeft size={16} className="mr-1" /> Back
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-10 space-y-3">
          <h1 className="text-3xl sm:text-4xl font-serif" data-testid="coaching-apply-heading">Apply for Private Coaching</h1>
          <p className="text-muted-foreground leading-relaxed max-w-lg mx-auto">
            Private coaching with Mo is by application only. Share a little about where you are and what you're looking for, and Mo will follow up personally.
          </p>
        </div>

        <Card className="p-6 sm:p-8 rounded-2xl" data-testid="coaching-application-form">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name <span className="text-red-400">*</span></label>
                <input
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-accent/30"
                  data-testid="apply-name"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Email <span className="text-red-400">*</span></label>
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-accent/30"
                  data-testid="apply-email"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">What are your goals right now? <span className="text-red-400">*</span></label>
              <textarea
                name="goals"
                value={formData.goals}
                onChange={handleChange}
                placeholder="What are you hoping to achieve or work through?"
                rows={3}
                className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"
                data-testid="apply-goals"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">What brought you to coaching? <span className="text-red-400">*</span></label>
              <textarea
                name="what_brought_you"
                value={formData.what_brought_you}
                onChange={handleChange}
                placeholder="What's happening in your life that led you here?"
                rows={3}
                className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"
                data-testid="apply-what-brought"
                required
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Availability <span className="text-red-400">*</span></label>
                <select
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-accent/30"
                  data-testid="apply-availability"
                  required
                >
                  <option value="">Select your availability</option>
                  <option value="weekday_mornings">Weekday mornings</option>
                  <option value="weekday_afternoons">Weekday afternoons</option>
                  <option value="weekday_evenings">Weekday evenings</option>
                  <option value="weekends">Weekends</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Budget expectations <span className="text-red-400">*</span></label>
                <select
                  name="budget_expectations"
                  value={formData.budget_expectations}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-accent/30"
                  data-testid="apply-budget"
                  required
                >
                  <option value="">Select range</option>
                  <option value="under_100">Under $100/month</option>
                  <option value="100_200">$100 — $200/month</option>
                  <option value="200_500">$200 — $500/month</option>
                  <option value="500_plus">$500+/month</option>
                  <option value="open">Open to discussion</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Anything else you'd like to share?</label>
              <textarea
                name="additional_info"
                value={formData.additional_info}
                onChange={handleChange}
                placeholder="Optional — anything that would help Mo understand your situation better"
                rows={2}
                className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"
                data-testid="apply-additional"
              />
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full py-6 text-base"
                data-testid="submit-coaching-application"
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
                {!submitting && <Send size={16} className="ml-2" />}
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-3">
                Your information is kept confidential and only shared with Mo.
              </p>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
